import { useEffect, useRef, useState } from "react";
import styles from "./searchModalDesktop.module.scss";

import Input from "@Components/Input";
import { getSearchResult } from "./data/helper";
import SearchResultList from "./components/SearchResultList";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import SearchIcon from "@Icons/SearchIcon";
import { useHistory } from "hooks/useHistory";
import DefaultSuggestionsDesktop from "./components/DefaultSuggestionsDesktop";
import customHistoryPush from "@Utils/customHistoryPush";
import useTranslationHook from "hooks/useTranslationHook";
import DropDownLoader from "@Components/DropdownLoader";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import useIsIframe from "hooks/useIsIframe";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";

const SearchModalDesktop = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any>([]);
  const [inputValue, setInputValue] = useState("");
  const [showSearchResult, setShowSearchResult] = useState(false);
  const [showAnimateContainer, setShowAnimateContainer] = useState(true);
  const history = useHistory();
  const {
    isWhitelabel,
    partner_uuid,
    isDemoSubdomain,
    whitelabelColors,
    isRestrictedSearchEnabled,
    partner_id,
  } = useWhitelabel();
  const { isIframe } = useIsIframe();
  const useJavaApi = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.USE_JAVA_API_SEARCH);

  const timeout: any = useRef();
  const inputLength = inputValue.length;
  const modalRef = useRef<any>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const handleInputValueChange = (e: any) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    const value = e.trim().toLowerCase();
    setInputValue(e);
    if (value.length > 1) {
      setLoading(true);
      timeout.current = setTimeout(() => {
        (async () => {
          const res: any = await getSearchResult(
            e,
            isWhitelabel,
            isRestrictedSearchEnabled,
            partner_id,
          );
          setItems(res.data.data);
        })();
        setLoading(false);
      }, 200);
    } else {
      setItems([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        (event.type == "mousedown" &&
          modalRef.current &&
          !modalRef.current.contains(event.target)) ||
        (event.type == "keydown" && event.key === "Escape")
      ) {
        setShowSearchResult(false);
        setSelectedIndex(-1);
        document.getElementById("heroSectionSearchBar")?.blur();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleClickOutside);
    };
  }, []);

  const isSuggestionsVisible = inputLength === 0;

  const handleOnClick = (data: any) => {
    let redirectLink = `/places/${data?.type != "inventory" ? "search/" : ""}${
      data?.canonical_name
    }`;
    if (isWhitelabel) {
      redirectLink = convertToWhitelabelURL(redirectLink, isDemoSubdomain, partner_uuid);
    }
    gtagEventEmitter({
      action: "clicked_searched_result",
      category: PAGE.SEARCH,
      label: `${redirectLink}, 1, ${inputValue}`,
      canonical_name: data.canonical_name,
      use_java_api: useJavaApi,
    });

    // For whitelabel in iframe, open inventory in new tab
    if (isWhitelabel && isIframe && data.type === "inventory") {
      window.open(redirectLink, "_blank", "noopener,noreferrer");
    } else {
      customHistoryPush(history, redirectLink);
    }
  };

  const getItemProps: any = ({ data }: { data: any }) => ({
    onClick: () => handleOnClick(data),
  });

  const handleKeyDown = (e: any) => {
    const listItemHeight = 73.29;
    if (e.key === "Enter" && items && items?.length > 0) {
      if (selectedIndex < 0) {
        handleOnClick(items?.[0]);
      } else handleOnClick(items?.[selectedIndex]);
    }
    if (items?.length) {
      const container: any = document.getElementById(`searchresultUl`);
      if (e.key == "ArrowDown" && items.length > selectedIndex + 1) {
        setSelectedIndex((si) => si + 1);
        const itemVisible = isVisible(
          document.getElementById(`searchListItem${selectedIndex + 2}`),
          container,
        );
        if (!itemVisible) container?.scrollTo(0, (selectedIndex - 2) * listItemHeight);
      } else if (e.key == "ArrowUp" && selectedIndex > 0) {
        setSelectedIndex((si) => si - 1);
        const itemVisible = isVisible(
          document.getElementById(`searchListItem${selectedIndex - 1}`),
          container,
        );
        if (!itemVisible) container?.scrollTo(0, (selectedIndex - 1) * listItemHeight);
      }
    }
  };

  const isVisible = (ele: any, container: any) => {
    const eleTop = ele?.offsetTop;
    const eleBottom = eleTop + ele?.clientHeight;

    const containerTop = container?.scrollTop;
    const containerBottom = containerTop + container?.clientHeight;

    // The element is fully visible in the container
    return (
      (eleTop >= containerTop && eleBottom <= containerBottom) ||
      // Some part of the element is visible in the container
      (eleTop < containerTop && containerTop < eleBottom) ||
      (eleTop < containerBottom && containerBottom < eleBottom)
    );
  };

  const handleOnFocus = () => {
    setShowSearchResult(true);
    gtagEventEmitter({
      action: "clicked_search_bar",
      category: PAGE.SEARCH,
      label: "search_bar",
      use_java_api: useJavaApi,
    });
  };

  const renderSearchResult = () => (
    <>
      {inputLength < 2 && inputLength > 0 && items?.length === 0 && (
        <div className={styles.defTextContainer}>
          <i className={`icon-keyboard ${styles.keyboardIcon}`} />
          <div data-testid="search-message" className={styles.defText}>
            {t("You can find your accommodation more easily if you add")} {2 - inputLength}{" "}
            {t("more alphabet")}
            {2 - inputLength > 1 ? "s" : ""}
          </div>
        </div>
      )}
      {isSuggestionsVisible && <DefaultSuggestionsDesktop gtagEventCategory={PAGE.SEARCH} />}
      {!isSuggestionsVisible && loading && !(inputLength < 2) ? (
        <DropDownLoader />
      ) : (
        <SearchResultList selectedIndex={selectedIndex} items={items} getItemProps={getItemProps} />
      )}
    </>
  );

  const { t } = useTranslationHook();
  const searchPlaceHolderText = t("home.searchinputhome.searchmodaldesktop.searchplaceholder");
  const searchText = t("home.searchinputhome.searchmodaldesktop.searchbyprefix");
  const cityText = t("home.searchinputhome.searchmodaldesktop.citysuffix");
  const universityText = t("home.searchinputhome.searchmodaldesktop.universitysuffix");
  const propertyText = t("home.searchinputhome.searchmodaldesktop.propertysuffix");
  const cityUnivOrPropText = t("home.searchinputhome.searchmodaldesktop.cityuniversityorproperty");

  return (
    <div className={formatClassNames(styles.desktop)} data-testid="desktop-search-section">
      <div className={styles["searchContainer"]}>
        <Input
          type="text"
          label=""
          className={styles["searchInput"]}
          placeholder={searchPlaceHolderText}
          value={inputValue}
          onChange={handleInputValueChange}
          showClearInnput
          autoFocus={false}
          onFocus={() => handleOnFocus()}
          showPlaceholder
          onKeyDown={handleKeyDown}
          isHomePageDesktop
          id="heroSectionSearchBar"
          dataTestId="heroSectionSearchBar"
        />
        {showAnimateContainer && (
          <div
            className={styles.animateContainer}
            role="none"
            onClick={() => {
              setShowAnimateContainer(false);
              document.getElementById("heroSectionSearchBar")?.focus();
            }}
          >
            <span className={styles.staticWord}>{searchText} </span>
            <div className={styles.animateWordsWrapper}>
              <span className={styles.animateWords}>{cityText}</span>
              <span className={styles.animateWords}>{universityText}</span>
              <span className={styles.animateWords}>{propertyText}</span>
              <span className={styles.animateWords}>{cityUnivOrPropText}</span>
            </div>
          </div>
        )}
        {!inputValue && (
          <i
            data-testid="search-icon"
            className={formatClassNames(
              styles.searchIcon,
              isWhitelabel ? styles.whitelabelSearchIcon : "",
            )}
            onClick={handleOnFocus}
            role="none"
          >
            <SearchIcon
              fill={isWhitelabel ? whitelabelColors?.primary_text_color : "#ffffff"}
              size="100%"
            />
          </i>
        )}
      </div>
      {showSearchResult && (
        <div className={styles.desktopModal} id="searchresultUl" ref={modalRef}>
          {renderSearchResult()}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(SearchModalDesktop, {
  componentName: "SearchModalDesktop-Global",
  showForChina: true,
});
